// Queue (owner, 2026-09-28: "it cant all rely on a single worker, especially in a multi user app"; ruling 7,
// "Audit section (Recommended)"; ADR 0013). What the worker's slots run, what waits, for how long, and why —
// the claim's own reason — read under the audit section like Costs. Four bearings (slots, Slack, the
// longest live wait, the AI service), then three tables: running, waiting (by kind, live or bulk), and the
// last 24 hours per kind. Kinds, states, times and names only: a job's contents never reach this page.
// Every column sorts, in the page (the rows are few and fully loaded); the tables are a view with a route
// behind it, so the generated-table export floor (04-data §1.14) does not apply.
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { AiCallPersonDto, QueueKindStatsDto, QueueNextJobDto, QueueResponse, QueueRunningJobDto, QueueWaitingGroupDto } from '../../shared/api-types.js';
import { QUEUE_RUN_STATE_LABELS, type QueueRunState } from '../../shared/enums.js';
import { DELETED_ACCOUNT_WORDING } from '../../shared/text.js';
import { AdminSectionStrip } from '../shell/admin.js';
import { useMe } from '../shell/me-context.js';
import {
  Banner,
  Button,
  DataTable,
  FailureView,
  Page,
  PageHead,
  RelativeTime,
  Section,
  StatTile,
  StatTileRow,
  StatusPill,
  browserTimeZone,
  formatCount,
  formatTime,
  pluralise,
  timeZoneLabel,
  useRequest,
  type DataTableColumn,
  type DataTableStatus,
  type RequestState,
} from '../ui/index.js';
import { adminApi } from './admin-api.js';
import {
  RUN_STATE_HINTS,
  RUN_STATE_TONES,
  attemptText,
  durationText,
  heartbeatMs,
  jobKindLabel,
  jobKindRank,
  ofText,
  queueStatusLine,
  runStateRank,
  skipReasonText,
  waitReasonRank,
  waitReasonText,
  workLabel,
  workRank,
} from './queue-helpers.js';
import './admin.css';

function statusOf<T>(request: RequestState<T>, rowCount: number): DataTableStatus {
  if (request.status === 'loading') return { kind: 'loading', rowCount };
  if (request.status === 'error' && request.error) return { kind: 'error', failure: request.error, onRetry: request.reload };
  return { kind: 'ready' };
}

export function PersonCell({ person, usersView }: { person: AiCallPersonDto | null; usersView: boolean }) {
  // Why the shared wording (the owner's ruling Q16 of escalation 20261005-200526, "Deleted account"): Costs and the audit
  // log already said "Deleted account" for a person who no longer resolves; one thing is never worded two ways.
  if (!person) return <span className="muted">{DELETED_ACCOUNT_WORDING}</span>;
  return (
    <span className="audit-actor">
      {usersView ? <Link to={`/admin/users/${encodeURIComponent(person.id)}`} className="hit-area">{person.displayName}</Link> : <span>{person.displayName}</span>}
      <span className="audit-actor__email">{person.email}</span>
    </span>
  );
}

const personName = (person: AiCallPersonDto | null): string | null => person?.displayName ?? null;

/**
 * The Next column's line under the person: asked for now, a first attempt, or the attempt that failed — each a complete
 * string, the failure's code a placeholder in it, never a clause spliced onto the sentence (05-content §8).
 */
export function nextJobLine(next: Pick<QueueNextJobDto, 'priority' | 'attempts' | 'lastErrorCode'>): string {
  if (next.priority === 1) return 'Asked for now';
  if (!(next.attempts > 0)) return 'First attempt';
  const attempts = formatCount(next.attempts);
  return next.lastErrorCode ? `Attempt ${attempts} failed · ${next.lastErrorCode}` : `Attempt ${attempts} failed`;
}

/** A kind's skips by reason (05-content §6: a skipped run carries its reason beside the word); nothing when none. */
export function SkipReasons({ row }: { row: QueueKindStatsDto }) {
  if (row.skipReasons.length === 0) return null;
  return (
    <div className="stack stack--1">
      <p className="text-xs secondary">Why skipped</p>
      <ul className="cluster text-sm" role="list" aria-label={`Why ${jobKindLabel(row.kind)} jobs were skipped`}>
        {row.skipReasons.map((reason) => (
          <li key={reason.code} data-skip-reason={reason.code}>
            {skipReasonText(reason.code)} <span className="secondary">· {formatCount(reason.count)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The Running table's State cell: 05-content §6's Running, with where the job stands against its lease as a qualifier
 * beside it — a reading, never a word of its own in the State column (§6.1, ruled A25 in design system 8.5.0;
 * `QUEUE_RUN_STATE_LABELS`). The pill's title says what the position means.
 */
export function RunStatePill({ state }: { state: QueueRunState }) {
  return <StatusPill tone={RUN_STATE_TONES[state]} title={RUN_STATE_HINTS[state]}>{QUEUE_RUN_STATE_LABELS[state]}</StatusPill>;
}

export default function AdminQueuePage() {
  const me = useMe();
  const usersView = me.admin.users.view;
  const timeZone = browserTimeZone();
  const zoneLabel = timeZoneLabel(timeZone);
  const queue = useRequest<QueueResponse>((signal) => adminApi.queue(signal), []);
  const data = queue.data;
  const inThisProcess = data?.worker.inThisProcess ?? false;

  // Folding (04-data §1.7; ADR 0168), for the two tables wider than their room from --bp-md: a running job's lease and
  // heartbeat (scanning) first, then its attempt and kind of work, then when it started, its state and whose it is; the
  // day's percentiles before its medians, and the counts a kind is compared by last. The type names the row and never
  // folds. The pending table fits as measured and declares none.
  const runningColumns = useMemo<DataTableColumn<QueueRunningJobDto>[]>(() => [
    { key: 'kind', header: 'Type', type: 'enum', card: 'title', sortValue: (row) => jobKindRank(row.kind), cell: (row) => jobKindLabel(row.kind) },
    { key: 'person', header: 'Person', fold: 7, sortValue: (row) => personName(row.person), cell: (row) => <PersonCell person={row.person} usersView={usersView} /> },
    {
      key: 'state',
      header: 'State',
      type: 'enum',
      fold: 6,
      sortValue: (row) => runStateRank(row.state),
      cell: (row) => <RunStatePill state={row.state} />,
    },
    { key: 'work', header: 'Work', type: 'enum', fold: 4, sortValue: (row) => workRank(row), cell: (row) => workLabel(row) },
    { key: 'attempts', header: 'Attempt', type: 'number', numeric: true, fold: 3, sortValue: (row) => row.attempts, cell: (row) => attemptText(row.attempts, row.maxAttempts) },
    { key: 'claimed_at', header: 'Started', type: 'date', card: 'meta', fold: 5, sortValue: (row) => (row.claimedAt === null ? null : Date.parse(row.claimedAt)), cell: (row) => <RelativeTime value={row.claimedAt} timeZone={timeZone} /> },
    { key: 'heartbeat', header: 'Last heartbeat', type: 'date', fold: 2, sortValue: (row) => heartbeatMs(row), cell: (row) => <RelativeTime value={row.lastHeartbeatAt} timeZone={timeZone} /> },
    {
      key: 'lease',
      header: `Lease ends (${zoneLabel})`,
      name: 'Lease ends',
      type: 'date',
      fold: 1,
      headerTitle: 'When another claim may take the job over: its last heartbeat plus its kind\'s lease',
      sortValue: (row) => (row.leaseEndsAt === null ? null : Date.parse(row.leaseEndsAt)),
      cell: (row) => (row.leaseEndsAt === null ? '—' : <time dateTime={row.leaseEndsAt}>{formatTime(row.leaseEndsAt, { timeZone })}</time>),
    },
  ], [usersView, timeZone, zoneLabel]);

  const waitingColumns = useMemo<DataTableColumn<QueueWaitingGroupDto>[]>(() => [
    { key: 'kind', header: 'Type', type: 'enum', card: 'title', sortValue: (row) => jobKindRank(row.kind), cell: (row) => jobKindLabel(row.kind) },
    { key: 'work', header: 'Work', type: 'enum', sortValue: (row) => (row.bulk ? 2 : 1), cell: (row) => (row.bulk ? 'Bulk (a refresh)' : 'Live') },
    { key: 'due', header: 'Due jobs', type: 'number', numeric: true, sortValue: (row) => row.due, cell: (row) => formatCount(row.due) },
    { key: 'wait', header: 'Longest wait', type: 'number', numeric: true, headerTitle: 'How long the longest-waiting of them has been due', sortValue: (row) => row.oldestWaitMs, cell: (row) => durationText(row.oldestWaitMs) },
    { key: 'next', header: 'Next', sortValue: (row) => personName(row.next?.person ?? null), cell: (row) => (row.next ? <span className="stack stack--1"><PersonCell person={row.next.person} usersView={usersView} /><span className="text-xs muted">{nextJobLine(row.next)}</span></span> : '—') },
    {
      key: 'reason',
      header: 'Why it waits',
      type: 'enum',
      sortValue: (row) => waitReasonRank(row.next?.waitReason ?? null, inThisProcess),
      cell: (row) => waitReasonText(row.next?.waitReason ?? null, inThisProcess),
    },
  ], [usersView, inThisProcess]);

  const dayColumns = useMemo<DataTableColumn<QueueKindStatsDto>[]>(() => [
    { key: 'kind', header: 'Type', type: 'enum', card: 'title', sortValue: (row) => jobKindRank(row.kind), cell: (row) => jobKindLabel(row.kind) },
    { key: 'done', header: 'Completed', type: 'number', numeric: true, fold: 9, sortValue: (row) => row.done, cell: (row) => formatCount(row.done) },
    { key: 'failed', header: 'Failed', type: 'number', numeric: true, fold: 8, sortValue: (row) => row.failed, cell: (row) => formatCount(row.failed) },
    // Why a column of its own (05-content §6; migration 0050): a job that looked and found nothing to do is not
    // Completed — the column exists to tell applied from not applicable. Its reasons are the row's detail.
    { key: 'skipped', header: 'Skipped', type: 'number', numeric: true, fold: 7, headerTitle: 'Jobs that looked and found nothing to do; why is under the row', sortValue: (row) => row.skipped, cell: (row) => formatCount(row.skipped) },
    { key: 'refused', header: 'Refused', type: 'number', numeric: true, fold: 6, sortValue: (row) => row.refused, cell: (row) => formatCount(row.refused) },
    { key: 'retried', header: 'Retried', type: 'number', numeric: true, fold: 3, headerTitle: 'Jobs that needed more than one attempt', sortValue: (row) => row.retried, cell: (row) => formatCount(row.retried) },
    { key: 'wait_median', header: 'Wait (median)', type: 'number', numeric: true, fold: 4, headerTitle: 'From due to started, last attempt', sortValue: (row) => row.waitMedianMs, cell: (row) => durationText(row.waitMedianMs) },
    { key: 'wait_p95', header: 'Wait (95th pct.)', name: 'Wait (95th percentile)', type: 'number', numeric: true, fold: 1, sortValue: (row) => row.waitP95Ms, cell: (row) => durationText(row.waitP95Ms) },
    { key: 'run_median', header: 'Run (median)', type: 'number', numeric: true, fold: 5, headerTitle: 'From started to finished, last attempt', sortValue: (row) => row.runMedianMs, cell: (row) => durationText(row.runMedianMs) },
    { key: 'run_p95', header: 'Run (95th pct.)', name: 'Run (95th percentile)', type: 'number', numeric: true, fold: 2, sortValue: (row) => row.runP95Ms, cell: (row) => durationText(row.runP95Ms) },
  ], []);

  const loading = queue.status === 'loading';
  const cooling = data?.aiCoolingUntil ?? null;

  return (
    <Page measure="wide">
      <PageHead
        title="Queue"
        strip={<AdminSectionStrip />}
        actions={<Button variant="secondary" icon="refresh" onClick={queue.reload} loading={queue.refreshing === true}>Refresh</Button>}
      />

      {/* Why no account of the slots here: their count is the "Slots busy" tile, and the one-at-a-time rule reaches
          the reader as a wait reason exactly when it applies (ADR 0013). */}
      {/* Why nothing once the load has failed (app-baseline 05-client §5.4: no indefinite loading state; Phase 1c D18):
          the failure below says what failed, and "Loading the queue…" above it would say it is still coming. */}
      <p className="secondary text-sm" role="status">{queueStatusLine(data ? { asOf: formatTime(data.asOf, { timeZone }), zone: zoneLabel } : null, queue.status)}</p>

      {data && !data.worker.inThisProcess ? (
        <Banner tone="info" title={data.worker.enabled ? 'No worker runs in this process' : 'The worker is switched off'}>
          {data.worker.enabled
            ? 'The jobs below are read from the database, but why a job waits can only be told by the process that runs them.'
            : 'WORKER_ENABLED is off: queued jobs wait until a process with the worker on starts.'}
        </Banner>
      ) : null}

      {queue.status === 'error' && queue.error ? (
        <FailureView failure={queue.error} what="the queue" onRetry={queue.reload} compact />
      ) : (
        <StatTileRow minWidth="12rem">
          <StatTile
            label="Slots busy"
            value={data ? (data.busy ? ofText(data.busy.slots, data.capacity.slots) : '—') : null}
            loading={loading}
            hint={data?.busy ? `AI work ${ofText(data.busy.ai, data.capacity.aiCap)} · bulk ${ofText(data.busy.bulk, data.capacity.bulkCap)}` : undefined}
          />
          <StatTile
            label="Slack checks"
            value={data ? (data.busy ? ofText(data.busy.slackChecks, data.capacity.slackCap) : '—') : null}
            loading={loading}
            hint={
              data?.busy
                ? `Daily sweeps ${ofText(data.busy.slackSweeps, data.capacity.slackSweepCap)} · channel list refreshes ${ofText(data.busy.slackSyncs, data.capacity.slackSyncCap)}`
                : undefined
            }
          />
          <StatTile
            label="Longest live wait"
            value={data ? (data.oldestLiveWaitMs === null ? 'None waiting' : durationText(data.oldestLiveWaitMs)) : null}
            valueTone={data && data.oldestLiveWaitMs !== null && data.oldestLiveWaitMs > 15 * 60_000 ? 'warning' : 'default'}
            loading={loading}
            hint={data
              ? data.oldestWaitMs === null
                ? `${pluralise(data.dueLater, { one: '# job', other: '# jobs' })} due later`
                : `Any job: ${durationText(data.oldestWaitMs)} · ${pluralise(data.dueLater, { one: '# job', other: '# jobs' })} due later`
              : undefined}
          />
          <StatTile
            label="AI service"
            value={data ? (cooling ? 'Cooling down' : 'Available') : null}
            valueTone={cooling ? 'warning' : 'default'}
            loading={loading}
            hint={cooling ? `Asked Calis to wait until ${formatTime(cooling, { timeZone })}` : undefined}
          />
        </StatTileRow>
      )}

      <Section id="running" title="Running">
        <DataTable<QueueRunningJobDto>
          caption="Running jobs"
          failureNoun="the running jobs"
          columns={runningColumns}
          rows={data?.running ?? []}
          rowKey={(row) => row.id}
          status={statusOf(queue, 3)}
          sort={{ defaultSort: { key: 'claimed_at', dir: 'asc' }, namespace: 'running' }}
          clientSort
          empty={{ title: 'Nothing is running', description: 'A job appears here while a slot runs it.' }}
          footer={data && data.running.length > 0 ? <div className="audit-footer"><span>{pluralise(data.running.length, { one: '# job running', other: '# jobs running' })}</span></div> : undefined}
        />
      </Section>

      {/* Why "Pending" (05-content §6: waiting to start is Pending, "Waiting" on its Not-list; the owner's ruling O8 keeps
          §6 binding on this page): the heading names the state of the jobs beneath it, as "Running" beside it does — the
          state JOB_STATUS_LABELS gives a queued job. The section's anchor keeps its id. */}
      <Section id="waiting" title="Pending">
        <DataTable<QueueWaitingGroupDto>
          caption="Pending jobs"
          failureNoun="the pending jobs"
          columns={waitingColumns}
          rows={data?.waiting ?? []}
          rowKey={(row) => `${row.kind}:${row.bulk ? 'bulk' : 'live'}`}
          status={statusOf(queue, 2)}
          sort={{ defaultSort: { key: 'wait', dir: 'desc' }, namespace: 'waiting' }}
          clientSort
          empty={{ title: 'Nothing is waiting', description: 'Every due job has started.' }}
        />
      </Section>

      <Section id="last-day" title="Last 24 hours">
        <DataTable<QueueKindStatsDto>
          caption="Finished jobs in the last 24 hours"
          failureNoun="the jobs finished in the last 24 hours"
          columns={dayColumns}
          rows={data?.lastDay ?? []}
          rowKey={(row) => row.kind}
          status={statusOf(queue, 5)}
          sort={{ defaultSort: { key: 'kind', dir: 'asc' }, namespace: 'day' }}
          clientSort
          // Why skipped, by reason and count — never by person (the owner's ruling O2 of 2026-10-05).
          // Why null, not the element, for a kind with no skips (04-data §1.12; Phase 1c finding R17): DataTable draws a
          // detail row for any value it is handed, and an element whose component renders nothing is still a value — so
          // every kind without skips carried an empty detail row beneath it, an expansion that opens to an empty box.
          rowDetail={(row) => (row.skipReasons.length > 0 ? <SkipReasons row={row} /> : null)}
          detailFrom={1}
          empty={{ title: 'No job finished in the last 24 hours', description: 'A kind appears here once one of its jobs has finished.' }}
        />
      </Section>
    </Page>
  );
}
